/** @format */

import {ChevronLeftIcon, ChevronRightIcon} from '@heroicons/react/solid'
import React, {useMemo, type ChangeEvent, type PropsWithChildren} from 'react'

import {useFormApi, useFormspreeContext} from '@src/contexts'

import {useTableModel} from './TableModel'

export function Pagination() {
  const {updateDisplayConfig} = useFormApi()
  const {table} = useTableModel()
  const {pagination} = table.getState()
  const pageCount = useFormattedPageCount()

  async function handlePageSizeChange(
    event: ChangeEvent<HTMLSelectElement>,
  ): Promise<void> {
    const pageSize = +event.target.value
    table.setPageSize(pageSize)
    await updateDisplayConfig({pageSize})
  }

  return (
    <div className="flex items-center justify-between gap-x-4 border-t border-gray-100 p-2 sm:p-3">
      <div className="flex shrink-0 items-center gap-x-2 text-sm">
        <select
          className="border border-gray-200 bg-white px-1 py-0.5 focus:outline-none focus:ring-0 focus-visible:border-gray-400"
          onChange={handlePageSizeChange}
          value={pagination.pageSize}
        >
          {[10, 25, 50, 100].map(pageSize => (
            <option key={pageSize} value={pageSize}>
              {pageSize}
            </option>
          ))}
        </select>
        <p className="text-nowrap">per page</p>
      </div>

      <div className="flex shrink-0 items-center gap-x-4 text-sm">
        <p className="text-nowrap">
          <span>Page </span>
          <span>{pagination.pageIndex + 1}</span>
          <span> of </span>
          <span>{pageCount}</span>
        </p>
        <div className="flex items-center gap-x-2">
          <ChangePageButton
            disabled={!table.getCanPreviousPage()}
            label="Go to previous page"
            onClick={table.previousPage}
          >
            <ChevronLeftIcon />
          </ChangePageButton>
          <ChangePageButton
            disabled={!table.getCanNextPage()}
            label="Go to next page"
            onClick={table.nextPage}
          >
            <ChevronRightIcon />
          </ChangePageButton>
        </div>
      </div>
    </div>
  )
}

type ChangePageButtonProps = PropsWithChildren<{
  disabled: boolean
  label: string
  onClick: () => void
}>

function ChangePageButton(props: ChangePageButtonProps) {
  const {children, disabled, label, onClick} = props
  return (
    <button
      className="flex size-8 items-center justify-center rounded-md border border-gray-200 text-gray-700 transition-colors hover:border-gray-400 hover:text-gray-900 focus-visible:border-gray-400 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0"
      disabled={disabled}
      onClick={onClick}
      type="button"
    >
      {children}
      <span className="sr-only">{label}</span>
    </button>
  )
}

function useFormattedPageCount(): number | string {
  const {SUBMISSION_QUERY_LIMIT = 1000} = useFormspreeContext()
  const {table} = useTableModel()
  const value = table.getPageCount()
  const {pagination} = table.getState()
  return useMemo(() => {
    if (value < Number.MAX_SAFE_INTEGER) {
      return value
    }
    const count = Math.round(SUBMISSION_QUERY_LIMIT / pagination.pageSize)
    return `${count}+`
  }, [SUBMISSION_QUERY_LIMIT, value, pagination.pageSize])
}
